export interface DopSlideInfo {
	slideIndex: number; // 1-indexed
	totalSteps: number; // >= 1
}

export interface DopPosition {
	slide: number; // 1-indexed
	step: number; // 1-indexed within the slide
}

export type DopReconcilePlan =
	| { type: 'noop' }
	| { type: 'step'; commands: ('NextStep' | 'PreviousStep')[] }
	| { type: 'reload'; slideNumber: number; thenSteps: number };

// The viewer's `totalSteps` is the build count for the slide; `CurrentStep`
// echoes range 0..totalSteps inclusive (base state + each build), so the
// slide has totalSteps + 1 internal (1-indexed) step values. Verified against
// a live deck: a slide reporting `totalSteps:4` echoed `CurrentStep:4` at its
// final build.
/** Last valid 1-indexed step in a slide (defaults to 1 for unknown slides). */
export const dopTotalSteps = (slides: DopSlideInfo[], slide: number): number => {
	const found = slides.find((s) => s.slideIndex === slide);
	return found ? found.totalSteps + 1 : 1;
};

/** 1-based global step index: first step of the first slide === 1. */
export const dopLinearIndex = (slides: DopSlideInfo[], slide: number, step: number): number => {
	const priorSteps = slides
		.filter((s) => s.slideIndex < slide)
		.reduce((acc, s) => acc + s.totalSteps + 1, 0);
	return priorSteps + step;
};

const maxSlide = (slides: DopSlideInfo[]): number => (
	slides.reduce((max, s) => Math.max(max, s.slideIndex), 0)
);

// Navigation math (dopNextTarget/dopPrevTarget/dopLinearIndex) assumes slides
// carry contiguous 1-indexed `slideIndex` values (1..N), as DOP's AllInfo reports them.
/** Next linear position, or null if already at the end. */
export const dopNextTarget = (
	slides: DopSlideInfo[],
	slide: number,
	step: number,
): DopPosition | null => {
	if (step < dopTotalSteps(slides, slide)) return { slide, step: step + 1 };
	if (slide < maxSlide(slides)) return { slide: slide + 1, step: 1 };
	return null;
};

/** Previous linear position, or null if already at the start. */
export const dopPrevTarget = (
	slides: DopSlideInfo[],
	slide: number,
	step: number,
): DopPosition | null => {
	if (step > 1) return { slide, step: step - 1 };
	if (slide > 1) return { slide: slide - 1, step: dopTotalSteps(slides, slide - 1) };
	return null;
};

const stepCommands = (delta: number): ('NextStep' | 'PreviousStep')[] => (
	Array.from({ length: Math.abs(delta) }, () => (delta > 0 ? 'NextStep' : 'PreviousStep'))
);

/**
 * Plan how to drive an iframe currently at `applied` to `target`.
 * - Small linear gaps step (smooth, crosses boundaries).
 * - Large gaps, or cross-slide moves with no AllInfo, reload at the target slide.
 * `K` is the max linear gap that is stepped rather than reloaded.
 */
export const dopReconcilePlan = (
	applied: DopPosition,
	target: DopPosition,
	slides: DopSlideInfo[],
	K: number,
): DopReconcilePlan => {
	if (applied.slide === target.slide && applied.step === target.step) return { type: 'noop' };

	if (slides.length === 0) {
		if (applied.slide === target.slide) {
			return { type: 'step', commands: stepCommands(target.step - applied.step) };
		}
		return { type: 'reload', slideNumber: target.slide, thenSteps: target.step - 1 };
	}

	const delta = dopLinearIndex(slides, target.slide, target.step)
		- dopLinearIndex(slides, applied.slide, applied.step);

	if (Math.abs(delta) <= K) return { type: 'step', commands: stepCommands(delta) };
	return { type: 'reload', slideNumber: target.slide, thenSteps: target.step - 1 };
};
